import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ConfigStoreValues = {
  apiUrl: string;
  registryUrl: string;
  pluginVersion: string;
  appVersion: string;
  pluginUrl: string;
  pluginFromMarketplace: boolean;
  pluginMarketplaceUrl: string;
  previousAppVersion: string;
  pluginCoreVersion: string;
  pluginUIVersion: string;
};

export type ConfigStore = ConfigStoreValues & {
  setConfig: (config: Omit<ConfigStoreValues, 'previousAppVersion'>) => void;
  isAppVersionChanged: () => undefined | 'major' | 'minor' | 'patch';
};

const LOCAL_STORAGE_KEY = 'config-store';

export const useConfigStore = create(
  // TODO: remove persist once we have properly configured API requests so we can use hooks
  persist<ConfigStore>(
    (set, get) => ({
      apiUrl: '',
      registryUrl: '',
      pluginVersion: '',
      appVersion: '',
      pluginUrl: '',
      pluginFromMarketplace: false,
      pluginMarketplaceUrl: '',
      previousAppVersion: '',
      pluginCoreVersion: '',
      pluginUIVersion: '',

      setConfig: (config) => {
        const { appVersion, previousAppVersion } = get();

        let previousAppVersionToSet = previousAppVersion;

        if (previousAppVersion === '') {
          previousAppVersionToSet = config.appVersion;
        } else if (appVersion !== previousAppVersion) {
          previousAppVersionToSet = previousAppVersion;
        } else {
          previousAppVersionToSet = appVersion;
        }

        return set({
          ...config,
          previousAppVersion: previousAppVersionToSet,
        });
      },

      isAppVersionChanged: () => {
        const { appVersion, previousAppVersion } = get();

        if (!appVersion || !previousAppVersion) {
          return undefined;
        }

        const [major, minor, patch] = appVersion.split('.');
        const [previousMajor, previousMinor, previousPatch] =
          previousAppVersion.split('.');

        if (
          major > previousMajor ||
          minor > previousMinor ||
          patch > previousPatch
        ) {
          if (major > previousMajor) {
            return 'major';
          }

          if (minor > previousMinor) {
            return 'minor';
          }

          if (patch > previousPatch) {
            return 'patch';
          }
        }

        return undefined;
      },
    }),
    {
      name: LOCAL_STORAGE_KEY,
      version: 2,
      partialize: (state) =>
        // make sure that the `previousAppVersion` state is not persisted
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) => !['previousAppVersion'].includes(key)
          )
        ) as ConfigStore,
    }
  )
);

/**
 * @deprecated Use useConfigStore instead
 */
export const getConfigFromLocalStorage = (): ConfigStoreValues | null => {
  const config = localStorage.getItem(LOCAL_STORAGE_KEY);

  if (!config) {
    return null;
  }

  return JSON.parse(config).state;
};
